/* Copyright (c) 2019-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { isTizenAPIAvailable } from 'utils/lib/platform/tizen';
import { VegaHelperService } from 'utils/lib/platform/vega';

export class PlatformDetectorService {
    constructor() {}

    static isBrowserPlatform(): boolean {
        // We currently do not have full codepaths everywhere in crimson to handle Android platform. Until
        // then, we need to continue to run the browser code in those cases otherwise we will fail to load
        // the mall layout and other things which essentially make the app unusable. This is noted as a TODO
        // in SH-2493.
        return !('cefQuery' in window) && !PlatformDetectorService.isAndroidPlatform();
    }

    static isCefPlatform(): boolean {
        return 'cefQuery' in window;
    }

    static isAndroidPlatform(): boolean {
        return (
            typeof window['Android'] !== 'undefined' &&
            typeof window['Android']['isAndroid'] === 'function' &&
            window['Android']['isAndroid']()
        );
    }

    static isBrowserLikePlatform(): boolean {
        return PlatformDetectorService.isBrowserPlatform() || PlatformDetectorService.isAndroidPlatform();
    }

    static isNativeLikePlatform(): boolean {
        return PlatformDetectorService.isCefPlatform() || PlatformDetectorService.isAndroidPlatform();
    }

    static isPWAModeOnLGTV(): boolean {
        // LG TV will carry "PWA" keyword in user agent when GFN is launched as full-screen app.
        return /(NetCast|Web0S|Linux\/SmartTV).+PWA/.test(navigator.userAgent);
    }

    static isPWAModeOnSamsungTV(): boolean {
        // Samsung Webbrowser carrying 'SamsungBrowser' that hosted app doesn't have
        return isTizenAPIAvailable() && !navigator.userAgent.includes('SamsungBrowser');
    }

    static isPWAModeOnVegaTV(): boolean {
        // Vega (Amazon Fire TV) hosts the web app inside a native React Native WebView,
        // which is always a full-screen app context (no browser chrome). Detect it via
        // the vegaShellInfo flag that the Vega shell injects before content load.
        return VegaHelperService.isVegaWebViewAvailable();
    }

    // Parse user agent string to detect ARM based CPU. Needed for MVPC-6496
    // while we do not have ARM cpu support on Mac OS native client, expected
    // to be removed in the future.
    static isArmPlatform(): boolean {
        return window.navigator.userAgent.toLowerCase().includes(' arm');
    }

    static isTouchPlatform(): boolean {
        return !!window.Touch && navigator.maxTouchPoints > 0;
    }
}
